import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getEvents, createEvent, updateEvent, deleteEvent } from '../services/api';
import './AdminEvents.css';

const EMPTY_FORM = {
  title: '',
  date: '',
  end_date: '',
  location: '',
  description: '',
  image_url: '',
  is_recurring: false,
};

function toInputDatetime(iso) {
  if (!iso) return '';
  // slice to "YYYY-MM-DDTHH:MM" for datetime-local input
  return iso.slice(0, 16);
}

export default function AdminEvents() {
  const [events, setEvents] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = () =>
    getEvents()
      .then((data) => setEvents([...data].sort((a, b) => new Date(b.date) - new Date(a.date))))
      .catch(() => {});

  useEffect(() => { load(); }, []);

  const handleChange = (e) => {
    setError('');
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const startEdit = (ev) => {
    setEditingId(ev.id);
    setForm({
      title: ev.title,
      date: toInputDatetime(ev.date),
      end_date: toInputDatetime(ev.end_date),
      location: ev.location || '',
      description: ev.description || '',
      image_url: ev.image_url || '',
      is_recurring: ev.is_recurring || false,
    });
    setError('');
    window.scrollTo({ top: document.querySelector('.admin-form-section').offsetTop - 80, behavior: 'smooth' });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { setError('Le titre est obligatoire.'); return; }
    if (!form.date) { setError('La date est obligatoire.'); return; }

    setSaving(true);
    setError('');
    const payload = {
      title: form.title.trim(),
      date: new Date(form.date).toISOString(),
      end_date: form.end_date ? new Date(form.end_date).toISOString() : null,
      location: form.location.trim(),
      description: form.description.trim(),
      image_url: form.image_url.trim(),
      is_recurring: form.is_recurring,
    };
    try {
      if (editingId) {
        await updateEvent(editingId, payload);
      } else {
        await createEvent(payload);
      }
      await load();
      cancelEdit();
    } catch {
      setError('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteEvent(id);
      setConfirmDelete(null);
      if (editingId === id) cancelEdit();
      await load();
    } catch {
      setError('Erreur lors de la suppression.');
    }
  };

  const now = new Date();
  const upcoming = events.filter((e) => new Date(e.date) >= now);
  const past = events.filter((e) => new Date(e.date) < now);

  return (
    <div className="admin-page">
      <div className="admin-header">
        <div className="container">
          <Link to="/gestion-ecclesiale" className="admin-breadcrumb">← Tableau de bord</Link>
          <h1>⚙ Gestion des Événements</h1>
          <p>Créez, modifiez ou supprimez les événements affichés sur le site.</p>
        </div>
      </div>

      <div className="container admin-body">

        {/* ── Add / Edit Form ─────────────────────────────────────────── */}
        <section className="admin-section admin-form-section">
          <h2>{editingId ? '✏ Modifier l\'événement' : '+ Ajouter un événement'}</h2>
          <form className="admin-form" onSubmit={handleSubmit} noValidate>
            <div className="form-row">
              <div className="form-group form-group--wide">
                <label htmlFor="title">Titre <span className="required">*</span></label>
                <input
                  id="title" name="title" type="text"
                  value={form.title} onChange={handleChange}
                  placeholder="Ex: Culte du Dimanche Matin"
                />
              </div>
              <div className="form-group">
                <label htmlFor="location">Lieu</label>
                <input
                  id="location" name="location" type="text"
                  value={form.location} onChange={handleChange}
                  placeholder="Ex: Salle principale"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="date">Date et heure de début <span className="required">*</span></label>
                <input
                  id="date" name="date" type="datetime-local"
                  value={form.date} onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label htmlFor="end_date">Date et heure de fin</label>
                <input
                  id="end_date" name="end_date" type="datetime-local"
                  value={form.end_date} onChange={handleChange}
                />
              </div>
              <div className="form-group form-group--checkbox">
                <label className="checkbox-label">
                  <input
                    type="checkbox" name="is_recurring"
                    checked={form.is_recurring} onChange={handleChange}
                  />
                  Événement récurrent
                </label>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="description">Description</label>
              <textarea
                id="description" name="description"
                value={form.description} onChange={handleChange}
                rows={3} placeholder="Décrivez l'événement..."
              />
            </div>

            <div className="form-group">
              <label htmlFor="image_url">URL de l'image de couverture</label>
              <input
                id="image_url" name="image_url" type="url"
                value={form.image_url} onChange={handleChange}
                placeholder="https://images.pexels.com/..."
              />
              <p className="field-hint">Collez une URL directe vers une image (Pexels, votre hébergement, etc.).</p>
            </div>

            {form.image_url && (
              <div className="photo-preview">
                <span className="photo-preview__label">Aperçu</span>
                <img
                  src={form.image_url} alt="Aperçu"
                  className="photo-preview--rect"
                  onError={(e) => { e.target.style.display = 'none'; }}
                  onLoad={(e) => { e.target.style.display = 'block'; }}
                />
              </div>
            )}

            {error && <p className="admin-error">{error}</p>}
            <div className="form-actions">
              <button type="submit" className="btn-admin btn-save" disabled={saving}>
                {saving ? 'Enregistrement...' : editingId ? '💾 Enregistrer les modifications' : '➕ Ajouter l\'événement'}
              </button>
              {editingId && (
                <button type="button" className="btn-admin btn-cancel" onClick={cancelEdit}>
                  Annuler
                </button>
              )}
            </div>
          </form>
        </section>

        {/* ── Upcoming Events Table ───────────────────────────────────── */}
        <EventTable
          title={`Événements à venir (${upcoming.length})`}
          events={upcoming}
          editingId={editingId}
          confirmDelete={confirmDelete}
          onEdit={startEdit}
          onDelete={handleDelete}
          onConfirmDelete={setConfirmDelete}
        />

        {/* ── Past Events Table ───────────────────────────────────────── */}
        {past.length > 0 && (
          <EventTable
            title={`Événements passés (${past.length})`}
            events={past}
            past
            editingId={editingId}
            confirmDelete={confirmDelete}
            onEdit={startEdit}
            onDelete={handleDelete}
            onConfirmDelete={setConfirmDelete}
          />
        )}

      </div>
    </div>
  );
}

function EventTable({ title, events, past, editingId, confirmDelete, onEdit, onDelete, onConfirmDelete }) {
  if (events.length === 0) return null;
  return (
    <section className={`admin-section${past ? ' admin-section--muted' : ''}`}>
      <h2>{title}</h2>
      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Image</th>
              <th>Titre</th>
              <th>Date</th>
              <th>Lieu</th>
              <th>Type</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {events.map((ev) => {
              const d = new Date(ev.date);
              return (
                <tr key={ev.id} className={editingId === ev.id ? 'row-editing' : ''}>
                  <td className="td-thumb">
                    {ev.image_url ? (
                      <img src={ev.image_url} alt={ev.title} className="table-thumb table-thumb--rect" />
                    ) : (
                      <div className="table-thumb-placeholder table-thumb--rect">📅</div>
                    )}
                  </td>
                  <td className="td-name">{ev.title}</td>
                  <td className="td-date">
                    {d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })}
                    <br />
                    <span className="td-time">{d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</span>
                  </td>
                  <td className="td-location">{ev.location || <em className="no-val">—</em>}</td>
                  <td>
                    {ev.is_recurring
                      ? <span className="role-badge">Récurrent</span>
                      : <span className="role-badge role-badge--grey">Unique</span>}
                  </td>
                  <td className="td-actions">
                    <button className="btn-admin btn-edit" onClick={() => onEdit(ev)}>✏ Modifier</button>
                    {confirmDelete === ev.id ? (
                      <span className="confirm-row">
                        <span>Confirmer?</span>
                        <button className="btn-admin btn-confirm-yes" onClick={() => onDelete(ev.id)}>Oui</button>
                        <button className="btn-admin btn-confirm-no" onClick={() => onConfirmDelete(null)}>Non</button>
                      </span>
                    ) : (
                      <button className="btn-admin btn-delete" onClick={() => onConfirmDelete(ev.id)}>🗑 Supprimer</button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}
